// The analysis lane — the score commenting on itself (July 2026): local key
// regions, roman numerals and cadence brackets drawn in the lane ScoreView
// reserves BELOW each system (`renderSystemUnderlay`, the mirror of the
// arrangement's above-staff overlay). The music theory lives entirely in
// src/music/analysis.ts (pure, corpus-tested); this file only places it on
// the engraving via the same `xAtBeat` contract the overlay uses, so romans
// sit exactly under the chord symbols they explain. The hook that computes
// the model and bundles the ScoreView props is `useAnalysisLane` in
// scoreShared.ts — this file stays component-only for fast refresh.
//
// Notation follows the textbook: a bold key prefix at each region start
// (major "A♭:", minor lowercase "g:") and plain romans after it, so a
// modulation reads as "…V7 I∆  G: IIm V7 I∆" along the lane. Cadence cells
// get an under-bracket with upticks at both ends; a bracket that crosses a
// system break keeps the broken end open, exactly like a tie.

import type { ScoreSystemOverlayContext } from '@/components/ScoreView';
import type { HarmonicAnalysis, KeyRegion } from '@/music/analysis';
import type { SequenceChord } from '@/music/song';

/** The analysed tune, in the same written order the engraver draws. */
export interface AnalysisLaneModel extends HarmonicAnalysis {
  sequence: SequenceChord[];
}

/** Major keys read as-is ("A♭:"), minor lowercase ("g:") — the analysis
 *  convention, and shorter than "G minor" in a lane that sits under music. */
const keyPrefix = (region: KeyRegion): string =>
  region.minor ? `${region.label.replace(/ minor$/, '').toLowerCase()}:` : `${region.label}:`;

export function AnalysisLaneUnderlay({
  context,
  lane,
}: {
  context: ScoreSystemOverlayContext;
  lane: AnalysisLaneModel;
}) {
  const s = context.spaceSize;
  const romanY = context.top + 1.6 * s;
  const bracketY = context.top + 2.7 * s;
  const tick = 0.5 * s;

  // Every analysed chord this system draws, at the exact x of its symbol.
  const placed = lane.sequence
    .map((chord, index) => ({ chord, index, x: context.xAtBeat(chord.barIndex, chord.beat) }))
    .filter((item): item is { chord: SequenceChord; index: number; x: number } => item.x !== null);
  if (placed.length === 0) return null;
  const xAt = new Map(placed.map((item) => [item.index, item.x]));

  const barIndexes = context.measures.map((measure) => measure.barIndex);
  const firstBar = Math.min(...barIndexes);
  const lastBar = Math.max(...barIndexes);
  const cells = lane.cells.filter(
    (cell) =>
      lane.sequence[cell.from].barIndex <= lastBar && lane.sequence[cell.to].barIndex >= firstBar,
  );
  const prefixAt = new Map(lane.regions.map((region) => [region.from, keyPrefix(region)]));

  return (
    <g className="analysis-lane" data-analysis-system={context.systemIndex}>
      {placed.map(({ chord, index, x }) => (
        <text key={chord.id} x={x} y={romanY} fontSize={1.45 * s} className="analysis-roman">
          {prefixAt.has(index) && (
            <tspan className="analysis-key">{`${prefixAt.get(index)} `}</tspan>
          )}
          {lane.romans[index]}
        </text>
      ))}
      {cells.map((cell) => {
        const from = xAt.get(cell.from);
        const to = xAt.get(cell.to);
        // A missing endpoint means the cell continues on another system:
        // run the bracket to the lane's edge and leave that end open.
        const x1 = from !== undefined ? from : context.left;
        const x2 = to !== undefined ? to + 1.9 * s : context.right;
        if (x2 <= x1) return null;
        const d =
          (from !== undefined
            ? `M ${x1} ${bracketY - tick} L ${x1} ${bracketY} `
            : `M ${x1} ${bracketY} `) +
          `L ${x2} ${bracketY}` +
          (to !== undefined ? ` L ${x2} ${bracketY - tick}` : '');
        return (
          <path
            key={`${cell.kind}:${cell.from}`}
            d={d}
            className="analysis-cell"
            data-kind={cell.kind}
          />
        );
      })}
    </g>
  );
}
